import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { ListTree, Plus, Trash2 } from 'lucide-react';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { PlacementXpParameter } from '../../model';
import type { PlacementFormValue } from '../../placementFormSchema';

export function PlacementXpMappingsFields() {
  'use no memo';
  const { control, register, setValue, formState } = useFormContext<PlacementFormValue>();
  const { fields, append, remove } = useFieldArray({ control, name: 'xpParametersMappings' });
  const mappings = useWatch({ control, name: 'xpParametersMappings' });
  const selected = new Set(mappings.map((mapping) => mapping.xpParameter).filter(Boolean));
  return (
    <DashboardPageFormBody width="lg">
      <Controller
        control={control}
        name="overridePublisherXpParametersMapping"
        render={({ field }) => (
          <Label className="border-input flex items-center gap-3 rounded-md border p-4 text-sm font-normal">
            <Checkbox
              checked={field.value}
              onCheckedChange={(checked) => {
                field.onChange(checked === true);
              }}
            />
            {dashboardT('placements.overrideXp')}
          </Label>
        )}
      />
      <section className="border-input overflow-hidden rounded-md border">
        <header className="bg-muted/40 border-input flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold">{dashboardT('placements.xpMappings')}</h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {dashboardT('placements.xpDescription')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= 10}
            onClick={() => {
              append({ value: '', xpParameter: '' });
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {dashboardT('placements.addXp')}
          </Button>
        </header>
        {fields.length ? (
          <div className="divide-border divide-y">
            {fields.map((field, index) => {
              const mappingErrors = formState.errors.xpParametersMappings?.[index];
              const valueError = mappingErrors?.value?.message;
              const parameterError = mappingErrors?.xpParameter?.message;
              const valueErrorId = `placement-xp-value-error-${field.id}`;
              const parameterErrorId = `placement-xp-parameter-error-${field.id}`;
              return (
                <div
                  key={field.id}
                  className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_220px_36px] sm:items-start"
                >
                  <div className="grid grid-rows-[16px_36px_auto] gap-1.5">
                    <Label
                      className="h-4 text-[13px] leading-4 font-semibold"
                      htmlFor={`placement-xp-value-${field.id}`}
                    >
                      {dashboardT('placements.xpValue')}
                    </Label>
                    <Input
                      id={`placement-xp-value-${field.id}`}
                      className="h-9"
                      placeholder={dashboardT('placements.xpValuePlaceholder')}
                      aria-describedby={valueError ? valueErrorId : undefined}
                      aria-invalid={Boolean(valueError)}
                      {...register(xpMappingPath(index, 'value'))}
                    />
                    {valueError ? (
                      <p id={valueErrorId} role="alert" className="text-destructive text-xs">
                        {valueError}
                      </p>
                    ) : null}
                  </div>
                  <div className="grid grid-rows-[16px_36px_auto] gap-1.5">
                    <Label
                      className="h-4 text-[13px] leading-4 font-semibold"
                      htmlFor={`placement-xp-parameter-${field.id}`}
                    >
                      {dashboardT('placements.xpParameter')}
                    </Label>
                    <Select
                      value={mappings[index]?.xpParameter ?? ''}
                      onValueChange={(value) => {
                        setValue(
                          xpMappingPath(index, 'xpParameter'),
                          value as PlacementXpParameter,
                          { shouldDirty: true, shouldValidate: true },
                        );
                      }}
                    >
                      <SelectTrigger
                        id={`placement-xp-parameter-${field.id}`}
                        className="h-9"
                        aria-label={dashboardT('placements.xpParameter')}
                        aria-describedby={parameterError ? parameterErrorId : undefined}
                        aria-invalid={Boolean(parameterError)}
                      >
                        <SelectValue placeholder={dashboardT('placements.select')} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(PlacementXpParameter).map((option) => (
                          <SelectItem
                            key={option}
                            value={option}
                            disabled={
                              selected.has(option) && mappings[index]?.xpParameter !== option
                            }
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {parameterError ? (
                      <p id={parameterErrorId} role="alert" className="text-destructive text-xs">
                        {parameterError}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 sm:mt-[22px]"
                    aria-label={dashboardT('placements.removeXp')}
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-10 text-center">
            <ListTree className="text-muted-foreground mx-auto h-6 w-6" aria-hidden="true" />
            <p className="text-muted-foreground mt-2 text-sm">{dashboardT('placements.xpEmpty')}</p>
          </div>
        )}
      </section>
      {formState.errors.xpParametersMappings?.message ? (
        <p role="alert" className="text-destructive text-xs">
          {formState.errors.xpParametersMappings.message}
        </p>
      ) : null}
    </DashboardPageFormBody>
  );
}

function xpMappingPath<TField extends 'value' | 'xpParameter'>(
  index: number,
  field: TField,
): `xpParametersMappings.${number}.${TField}` {
  // RHF models field-array indexes as numeric template-literal paths.
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return `xpParametersMappings.${index}.${field}`;
}
